/* eslint-disable react-doctor/no-inline-exhaustive-style */
"use client";

import { useEffect, useId, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Search, Menu, X, ArrowDownToLine } from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/ui/logo";
import { Kbd } from "@/components/ui/tls/kbd";
import { Button } from "@/components/ui/button";

interface NavItem {
  name: string;
  href: string;
}

const navItems: NavItem[] = [
  { name: "Home", href: "/" },
  { name: "Sounds", href: "/sounds" },
  { name: "Create", href: "/create" },
  { name: "Guide", href: "/guide" },
];

// Additional items kept for mobile drawer (from old nav)
const extendedNavItems: NavItem[] = [
  ...navItems,
  { name: "Saved", href: "/sounds/saved" },
  { name: "For You", href: "/sounds/foryou" },
  { name: "Convert", href: "/convert" },
  { name: "AI Voice", href: "/voice" },
  { name: "Blog", href: "/blog" },
  { name: "FAQ", href: "/faq" },
];

// §12.10: `motion-safe:` withholds the scale press under reduced motion, so
// `motion-reduce:active:bg-surface-hover` fills the gap — background
// darkens instantly instead of scaling, still confirming the press.
const pressFeedbackClassName =
  "transition-[scale,background-color] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] motion-safe:active:scale-[0.97] motion-reduce:active:bg-surface-hover";

interface SiteNavProps {
  className?: string;
  /** Optional element rendered after the Search button on desktop and after
   *  the search icon on mobile. Used to inject the ThemeToggle from layout. */
  trailingSlot?: React.ReactNode;
}

// eslint-disable-next-line react-doctor/no-giant-component
export function SiteNav({ className, trailingSlot }: SiteNavProps) {
  // eslint-disable-next-line react-doctor/no-initialize-state
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const searchRef = useRef<HTMLButtonElement>(null);
  const mobileMenuId = useId();

  useEffect(() => {
    // §07.1 "Detach threshold: 24px of scroll — the detached state is a 1px
    // color-hairline bottom border, NOT a shadow."
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (href: string) => (href === "/" ? pathname === "/" : pathname.startsWith(href));

  return (
    <header
      // `@container` (container-type: inline-size) makes the header the query
      // container for the two rows below. The desktop/mobile switch used to be
      // a `lg:` media query, and `rem` inside a media query resolves against
      // the browser's INITIAL font size, so a page-level 200% root font left
      // the desktop row in place at 1440 and pushed "Install in 2 min" and
      // the theme toggle off the right edge behind the fixed header (launch
      // review, create-chrome P1). Inside a container query `rem` resolves
      // against the root element's actual font size, so the same 64rem
      // threshold now collapses to the hamburger row once the type is large
      // enough that the desktop row could not fit — 1440 / 64rem = 140% root.
      className={cn("sticky top-0 z-50 w-full @container", className)}
      data-site-nav
      // "false" (not simply absent) so the CSS selector can be
      // `[data-nav-detached="false"]` without an extra :not().
      data-nav-detached={isScrolled ? "true" : "false"}
      // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
      style={{
        // §07.1 "Height: 52". The permanent 1px bottom border (transparent at
        // rest, hairline when detached) used to sit OUTSIDE the 52px rows and
        // the header measured 53 on every page. Sizing the header itself at
        // --tls-nav-h with border-box keeps the border inside the box, so the
        // bar is 52 in both states and .tls-clears-nav's arithmetic is exact.
        height: "var(--tls-nav-h)",
        boxSizing: "border-box",
        // §07.1: "No background, no border and no shadow in its default
        // state — it sits on the panel it happens to be over, and only
        // gains a ground when it detaches on scroll." No glassmorphism
        // (PRODUCT.md: the chrome is quiet; blur is reserved for transient
        // overlays only). Detach is a hairline, never a box-shadow.
        background: isScrolled ? "var(--tls-canvas-soft)" : "transparent",
        borderBottom: isScrolled
          ? "1px solid var(--tls-border-subtle)"
          : "1px solid transparent",
        boxShadow: "none",
        transition:
          "background-color var(--motion-duration-fast) var(--motion-ease-standard), border-color var(--motion-duration-fast) var(--motion-ease-standard)",
        // Nav foreground tokens inherit the active theme. The homepage hero
        // uses the same theme-authored luminance family, so the transparent
        // header never needs an inversion escape hatch.
        "--tls-nav-ink": "var(--tls-fg-strong)",
        "--tls-nav-ink-muted": "var(--tls-fg-muted)",
        "--tls-nav-ink-subtle": "var(--tls-fg-subtle)",
        "--tls-nav-edge": "var(--tls-border-strong)",
      } as React.CSSProperties}
    >
      {/* ── Desktop ──────────────────────────────────────────────────────── */}
      {/* --tls-container-max (1440) + --tls-gutter (16/24/32) are the ONE
          page container every body reads (.tls-container), so the wordmark
          lands on the same left axis as the content beneath it (x=32 at
          1440) instead of the old clamp(16px,4vw,40px) reading at x=40. */}
      {/* §07.1 "Height: 52 desktop and mobile" — a fixed row height with
          centered children (not vertical padding sized off the tallest
          child), so the bar height stays deterministic regardless of which
          child renders. Every child here is ≤44px tall, so they center
          inside a 52px row with room to spare. --tls-nav-h below is the
          single source of truth other pages read for clearance. */}
      {/* `@5xl:` (container ≥ 64rem) rather than `lg:` — see the header's
          comment: the container query tracks the root font size, the media
          query does not. */}
      <div
        className="mx-auto hidden items-center gap-6 @5xl:flex"
        style={{ maxWidth: "var(--tls-container-max)", height: "var(--tls-nav-h)", paddingInline: "var(--tls-gutter)" }}
      >
        {/* Logo */}
        {/* Wrapper-only fix: the rendered mark+wordmark is shorter than the
            44px WCAG 2.5.5 touch target, so the hit area is padded here
            (invisible, doesn't touch the Logo's own render) rather than
            inside components/ui/logo.tsx, which this agent does not own. */}
        <Link prefetch={false}
          href="/"
          aria-label="Tesla Lock Sound: Home"
          className="shrink-0 tlsr-focus"
          style={{ display: "inline-flex", alignItems: "center", minHeight: 44 }}
        >
          <Logo
            textClassName="text-base text-[var(--tls-nav-ink)]"
            iconClassName="text-[var(--tls-brand-ink)]"
          />
        </Link>

        {/* Route pills */}
        <nav className="flex items-center gap-1 ml-4" aria-label="Primary">
          {navItems.map(item => {
            const active = isActive(item.href);
            return (
              <Link prefetch={false}
                key={item.name}
                href={item.href}
                aria-current={active ? "page" : undefined}
                // §02.4: painted pill 36, hit area padded to 44 through the
                // ::before box (targets are set by pointer, not by paint).
                className="tlsr-focus relative before:absolute before:inset-x-0 before:-inset-y-1 before:content-['']"
                data-cuelume-hover="tick"
                // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  minHeight: 36,
                  padding: "0 12px",
                  borderRadius: 999,
                  // Selected ground derives from the nav's own ink, so it
                  // remains legible over either authored theme surface. It
                  // does not spend the hairline token as a filled blob.
                  background: active
                    ? "color-mix(in srgb, var(--tls-nav-ink) 12%, transparent)"
                    : "transparent",
                  color: active ? "var(--tls-nav-ink)" : "var(--tls-nav-ink-muted)",
                  fontFamily: "var(--font-body, sans-serif)",
                  fontWeight: 500,
                  fontSize: "0.84375rem",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  transition:
                    "background var(--motion-duration-fast) var(--motion-ease-standard), color var(--motion-duration-fast) var(--motion-ease-standard)",
                }}
              >
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Search trigger */}
        <button
          ref={searchRef}
          type="button"
          aria-label="Search sounds"
          aria-keyshortcuts="Meta+K"
          onClick={() => {
            window.dispatchEvent(new Event("tls:cmdk-open"));
          }}
          // `bg-transparent` is a CLASS, not an inline style, on purpose: an
          // inline `background: transparent` outranks every class, including
          // `motion-reduce:active:bg-surface-hover`, which left reduced-motion
          // users with no press feedback at all (§12.10).
          className={cn(
            "tlsr-focus bg-transparent",
            pressFeedbackClassName
          )}
          data-cuelume-press="press"
          data-cuelume-release="scan"
          // §04.8 "TRIGGER · IN NAV": a bordered, unfilled control — 1px
          // edge, 8/12 padding, radius-0, 220 wide — not a filled pill.
          // `width` rather than `minWidth`: the trigger is the one flexible
          // item in the row, so at enlarged text it shrinks (its label is
          // already ellipsised) before the primary CTA can leave the viewport.
          // The previous version filled with var(--tls-surface) (a solid
          // dark blob even in light theme, PRODUCT.md principle 1: "the
          // chrome is quiet, the content is loud" says a control shouldn't
          // out-weigh the row of text links beside it) and used radius-999,
          // which §04.8 never specifies for this element (radius-999 is the
          // Pill primitive, §02.4 — reserved for the theme/language toggles
          // and filled CTAs, not this trigger).
          // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 12px",
            borderRadius: 0,
            width: 220,
            minWidth: 0,
            maxWidth: 280,
            minHeight: 44,
            flexShrink: 1,
            border: "1px solid var(--tls-nav-edge)",
            cursor: "pointer",
            color: "var(--tls-nav-ink-subtle)",
          }}
        >
          <Search style={{ width: 15, height: 15, color: "var(--tls-nav-ink-subtle)", flexShrink: 0 }} />
          <span
            style={{
              fontFamily: "var(--font-body, sans-serif)",
              fontSize: "0.8125rem",
              color: "var(--tls-nav-ink-subtle)",
              flex: 1,
              minWidth: 0,
              textAlign: "left",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Search sounds
          </span>
          {/* TES-839: decorative shortcut hint — excluded from the button's
              accessible name (which is the aria-label "Search sounds") so the
              visible text matches it (label-content-name-mismatch). The
              shortcut is still exposed via aria-keyshortcuts above. */}
          <span aria-hidden="true">
            <Kbd>⌘K</Kbd>
          </span>
        </button>

        {/* Theme toggle — quiet icon button (PRODUCT.md: no theatrical animation) */}
        {trailingSlot}

        {/* Install CTA: the nav's ONE primary, through the shared <Button>
            primitive (§02.1). Accent fill, not the ink fill it used to carry
            (ink fill is not a DS button variant and competed with every
            page's accent primary beneath it). size="sm" = 36 painted, hit
            area padded to 44 (§02.4), 12.5/700, press + DS ring included. */}
        <Button asChild size="sm" className="shrink-0 font-bold">
          <Link prefetch={false} href="/guide">
            <ArrowDownToLine aria-hidden="true" style={{ width: 13, height: 13 }} />
            Install in 2 min
          </Link>
        </Button>
      </div>

      {/* ── Mobile ───────────────────────────────────────────────────────── */}
      {/* Same --tls-gutter as the desktop row and every page body: one
          left axis for the chrome, not a separate reading. */}
      <div
        className="flex items-center justify-between @5xl:hidden"
        style={{ height: "var(--tls-nav-h)", paddingInline: "var(--tls-gutter)" }}
      >
        <Link prefetch={false}
          href="/"
          aria-label="Tesla Lock Sound: Home"
          className="tlsr-focus"
          style={{ display: "inline-flex", alignItems: "center", minHeight: 44, minWidth: 0 }}
        >
          <Logo
            textClassName="text-base text-[var(--tls-nav-ink)]"
            iconClassName="text-[var(--tls-brand-ink)]"
          />
        </Link>

        {/* minWidth 0: a flex item's default min-width is its content width,
            so this cluster could push the row past the viewport at 200% text
            (overflow-200 guard, /de/sounds 394 > 390). Letting it shrink
            keeps the row inside the bar; the icons keep their own 44px. */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          {/* Mobile search icon — fires same ⌘K handler */}
          <button
            type="button"
            aria-label="Search"
            aria-keyshortcuts="Meta+K"
            onClick={() => {
              window.dispatchEvent(new Event("tls:cmdk-open"));
            }}
            // Icon button = circle (44px square at the pill radius); 16px is
            // the sheet's leading-corner radius only (D-1). Ground is a class
            // so `motion-reduce:active:bg-surface-hover` can win (§12.10).
            className={cn(
              "rounded-full tlsr-focus bg-transparent",
              pressFeedbackClassName
            )}
            data-cuelume-press="press"
            data-cuelume-release="scan"
            // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
            style={{
              width: 44,
              height: 44,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--tls-nav-ink-muted)",
              border: "none",
              cursor: "pointer",
            }}
          >
            <Search style={{ width: 18, height: 18 }} />
          </button>

          {/* Theme toggle (mobile) */}
          {trailingSlot}

          {/* Mobile hamburger + sheet drawer */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
                // §07.1: "The menu toggle has aria-expanded and
                // aria-controls." Radix's own auto-wiring doesn't reach this
                // id because the trigger is a plain custom <button> (asChild),
                // so it's pointed at mobileMenuId explicitly below.
                aria-controls={mobileMenuId}
                data-icon-btn
                className={cn(
                  "nav-toggle-btn rounded-full tlsr-focus bg-transparent",
                  pressFeedbackClassName
                )}
                data-state={mobileMenuOpen ? "open" : "closed"}
                data-cuelume-toggle="toggle"
                // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
                style={{
                  width: 44,
                  height: 44,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--tls-nav-ink-muted)",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {/* Stacked icons swapped via CSS — replaces the Framer Motion
                    AnimatePresence rotation animation. Site-nav is in the
                    root layout, so removing Framer Motion here saves ~30-80ms
                    TBT/INP per page load on mobile hydration. */}
                <span className="nav-toggle-icon-stack" aria-hidden="true">
                  <Menu className="nav-toggle-icon nav-toggle-icon-menu" />
                  <X className="nav-toggle-icon nav-toggle-icon-x" />
                </span>
              </button>
            </SheetTrigger>

            <SheetContent
              id={mobileMenuId}
              side="bottom"
              className="max-h-[85dvh] overflow-hidden p-0"
              style={{
                background: "color-mix(in srgb, var(--tls-canvas-soft) 97%, transparent)",
                borderColor: "var(--tls-border-subtle)",
              }}
              // No `onOpenAutoFocus` override. Radix's FocusScope only traps
              // focus once it is INSIDE the content; preventing the initial
              // move left focus on the toggle, so Tab walked the page behind
              // the overlay and a keyboard user could never reach a menu
              // link (launch review P0 against §07.1 "Mobile menu traps focus
              // while open"). The default moves focus to the first link, the
              // trap engages, Esc closes and returns focus to the toggle.
            >
              {/* No decorative accent wash here: PRODUCT.md's accent rule is
                  "never a background or an image flood" (§Colour), and the
                  sheet already reads as separate from the page behind it via
                  its own border-l hairline plus the canvas-soft ground shift
                  (97% canvas-soft vs the page's canvas-0 ground). */}
              <SheetHeader
                className="relative border-b p-6 pb-4"
                style={{ borderColor: "var(--tls-border-subtle)" }}
              >
                <SheetTitle className="text-left">
                  <Logo
                    textClassName="text-base text-[var(--tls-fg-strong)]"
                    iconClassName="text-[var(--tls-brand-ink)]"
                  />
                </SheetTitle>
              </SheetHeader>

              <nav className="relative p-4" aria-label="Mobile navigation">
                {/* §07.1 mobile menu row: 52 tall, hairline divider between
                    rows, type-h4 at 19. Rule 1 (media-led, not card-led)
                    applies to this list: it is content navigation, so it
                    gets hairlines, not a per-row bordered pill. */}
                {extendedNavItems.map(item => {
                  const active = isActive(item.href);
                  return (
                    <Link prefetch={false}
                      key={item.name}
                      href={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      data-cuelume-hover="tick"
                      aria-current={active ? "page" : undefined}
                      // eslint-disable-next-line react-doctor/no-inline-exhaustive-style
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        height: 52,
                        borderBottom: "1px solid var(--tls-border-subtle)",
                        color: active ? "var(--tls-fg-strong)" : "var(--tls-fg-muted)",
                        fontSize: "1.1875rem",
                        fontWeight: 700,
                        letterSpacing: "-0.015em",
                        textDecoration: "none",
                        transition: "color var(--motion-duration-fast) var(--motion-ease-standard)",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {item.name}
                      </span>
                      <span aria-hidden="true" style={{ color: "var(--tls-fg-subtle)", fontSize: "0.875rem" }}>
                        ›
                      </span>
                    </Link>
                  );
                })}

                <div className="pt-4" style={{ borderTop: "1px solid var(--tls-border-subtle)" }}>
                  {/* Same primary as the desktop bar, same primitive. */}
                  <Button asChild className="w-full font-bold">
                    <Link prefetch={false} href="/guide" onClick={() => setMobileMenuOpen(false)}>
                      <ArrowDownToLine aria-hidden="true" style={{ width: 14, height: 14 }} />
                      Install in 2 min
                    </Link>
                  </Button>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
